'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Spinner, Table, TableBody, TableCell, TableHead, TableHeadCell, TableRow } from 'flowbite-react';
import ProtectedRoute from '../../components/ProtectedRoute.tsx';
import { useGetBedrijfByNameQuery, useGetBedrijvenQuery } from '../../lib/model/kantoordagApi.ts';
import CompanyConfigForm from './CompanyConfigForm.tsx';
import CreateCompanyDialog from './CreateCompanyDialog.tsx';

export default function CompanyAdmin() {
    return (
        <ProtectedRoute authenticated roles={['ADMIN']}>
            <CompanyAdminInternal/>
        </ProtectedRoute>
    )
}

function CompanyAdminInternal() {
    const { t } = useTranslation('company-admin');
    const { data: companyNames, isLoading, isError, refetch } = useGetBedrijvenQuery();
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [selectedName, setSelectedName] = useState<string | undefined>(undefined);

    return (
        <Card className="mx-auto mt-4 max-w-4xl">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">{t('title')}</h1>
                <Button color="secondary" onClick={() => setShowCreateDialog(true)}>
                    {t('create_company')}
                </Button>
            </div>

            {isLoading && <Spinner/>}
            {isError && <p className="text-red-600">{t('loading_failed')}</p>}

            {companyNames != null && (
                companyNames.length === 0 ? (
                    <p className="text-gray-500">{t('no_companies')}</p>
                ) : (
                    <Table data-testid="company-admin-table">
                        <TableHead>
                            <TableRow>
                                <TableHeadCell>{t('logo')}</TableHeadCell>
                                <TableHeadCell>{t('name')}</TableHeadCell>
                                <TableHeadCell>{t('domain')}</TableHeadCell>
                                <TableHeadCell>{t('building')}</TableHeadCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className="divide-y">
                            {companyNames.map(name => (
                                <CompanyRow
                                    key={name}
                                    name={name}
                                    selected={name === selectedName}
                                    onSelect={() => setSelectedName(current => current === name ? undefined : name)}
                                />
                            ))}
                        </TableBody>
                    </Table>
                )
            )}

            {selectedName != null && (
                <div data-testid="company-admin-detail" className="mt-4">
                    <CompanyConfigForm
                        key={selectedName}
                        name={selectedName}
                        onSaved={() => refetch()}
                        onDeleted={() => {
                            setSelectedName(undefined);
                            refetch();
                        }}
                    />
                </div>
            )}

            <CreateCompanyDialog
                show={showCreateDialog}
                onClose={() => setShowCreateDialog(false)}
                onCreated={companyName => {
                    refetch();
                    setSelectedName(companyName);
                }}
            />
        </Card>
    )
}

interface CompanyRowProps {
    name: string;
    selected: boolean;
    onSelect: () => void;
}

// Clicking a row selects it for editing (CompanyConfigForm below) -- chosen over a BuildingAdmin.tsx-style
// select dropdown because this screen already renders every company as a full table row (T020/T010),
// so a click on the row itself is a more direct affordance than adding a second, redundant selector.
function CompanyRow({ name, selected, onSelect }: CompanyRowProps) {
    const { t } = useTranslation('company-admin');
    // GET /bedrijf only returns names (kept unchanged by this feature, contracts/company-admin-api.md) --
    // details (domain/building/logo) come from a per-company lookup, mirroring how the name list and
    // detail views are already split in the backend.
    const { data: company, isLoading, isError } = useGetBedrijfByNameQuery({ name });

    return (
        <TableRow
            data-testid="company-admin-row"
            onClick={onSelect}
            className={`cursor-pointer ${selected ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
        >
            <TableCell>
                {company != null && company.logo !== '' && (
                    <img src={company.logo} alt={company.naam} className="h-8 w-8 object-contain"/>
                )}
            </TableCell>
            <TableCell className="font-medium">{name}</TableCell>
            <TableCell>
                {isLoading && <Spinner size="sm"/>}
                {isError && <span className="text-red-600">{t('loading_failed')}</span>}
                {company?.domain}
            </TableCell>
            <TableCell>{company?.building}</TableCell>
        </TableRow>
    )
}
